import { useEffect, useState, useMemo } from "react";
import { useAuth } from "@/providers/auth-provider";
import { useSPAContext } from "@/providers/spa-provider";
import { getCollections } from "@/services/collection/get-collections";
import { CollectionData } from "types/web-app";
import { StandardFrame } from "@/components/ui/frames/standard-frame";
import { SimpleButton } from "@/components/ui/buttons/simple-button";
import { RouteNames } from "@/types/route-names";
import { Alert } from "@/components/alert/alert";
import { BackIcon } from "@/icons";
import { COLLECTIONS } from "game/data/collections";

export const CollectionListView = () => {
  const { getAccessToken } = useAuth();
  const { setCurrentRoute } = useSPAContext();
  const [collections, setCollections] = useState<CollectionData[]>([]);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    const fetchCollections = async () => {
      const token = getAccessToken();
      if (!token) return;

      setIsLoading(true);
      try {
        const response = await getCollections(token);
        if (response.ok) {
          setCollections(response.data.collections);
        }
      } catch (error) {
        console.error(error);
        Alert("Failed to fetch collections");
      } finally {
        setIsLoading(false);
      }
    };
    fetchCollections();
  }, []);

  const mergedCollections = useMemo(() => {
    return COLLECTIONS.map((staticCol) => {
      const apiCol = collections.find((c) => c.id === staticCol.id);
      return {
        ...staticCol,
        total_slots: staticCol.slots.length,
        obtained_count: apiCol?.obtained_count || 0,
      };
    });
  }, [collections]);

  return (
    <div className="w-full  p-1 flex flex-col gap-4">
      <StandardFrame variant="purple" className="flex flex-col gap-4 h-full">
        <div className=" justify-start overflow-y-auto flex flex-col gap-3 p-1">
          {mergedCollections.map((col) => (
            <div
              key={col.id}
              className="bg-pk-gray-2 border-2 border-pk-gray-4 rounded-lg p-3 flex flex-col gap-2 cursor-pointer hover:bg-pk-gray-1 active:scale-95 transition-all"
              onClick={() => {
                window.localStorage.setItem("selected_collection_id", col.id);
                setCurrentRoute(RouteNames.COLLECTION_DETAILS);
              }}
            >
              <div className="flex items-center gap-3">
                <img
                  src={`/assets/icons/books/${col.region?.toLowerCase()}.png`}
                  className="w-20 h-20 absolute object-contain"
                  alt={col.region}
                />
                <div className="flex-1 flex flex-col gap-1 pl-20">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-lg">
                      {col.display_name}
                    </span>
                    <span className="text-sm font-bold text-white/70">
                      {col.obtained_count} / {col.total_slots}
                    </span>
                  </div>
                  {/* Progress Bar */}
                  <div className="w-full h-2.5 bg-black/40 rounded-full overflow-hidden border border-white/5">
                    <div
                      className="h-full bg-gradient-to-r from-pk-green-4 to-pk-green-3 transition-all duration-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]"
                      style={{
                        width: `${
                          (col.obtained_count / col.total_slots) * 100
                        }%`,
                      }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </StandardFrame>
    </div>
  );
};
